<script setup>
import { deleteSystemFile, getSystemFile, selectFile } from '@/utils/file';
import { fieldEmits, fieldProps, useField } from '@/composables/form/field';

import FieldContainer from '../FieldContainer.vue';
import { useToast } from '@/plugins/toast';
import Upload from '@/db/models/upload';
import { createUpload, startUpload } from '@/services/upload';

const props = defineProps(fieldProps({
    accept: {
        required: false,
        type: [String, Array]
    },
    isPrivate: {
        type: Boolean,
        default: false,
    },
    multiple: {
        type: Boolean,
        default: false,
    },
    max: {
        type: [String, Number],
        default: 10,
    },
    disablePendingUploads: {
        type: Boolean,
        default: false,
    }
}));

const toast = useToast();
const emit = defineEmits(fieldEmits());

const castStateUsing = (state) => {
    if (!state) {
        return [];
    }

    return Array.isArray(state) ? state : [state];
}

const beforeStateDehydrated = async () => {
    return await component.uploadPendingFiles();
}

const dehydrateStateUsing = ({ dehydrated }) => {
    const mapState = (data) => {
        if (props.disablePendingUploads) {
            return data;
        }

        return { id: data.id };
    };

    if (props.multiple) {
        return dehydrated.map(mapState);
    }

    return mapState(dehydrated[0]);
}

const field = useField({
    props,
    emit,
    castStateUsing,
    beforeStateDehydrated,
    dehydrateStateUsing,
    component: () => {
        return component;
    },
});

const component = {
    getMaxFiles() {
        return field.props.max ?? 10;
    },
    get field() {
        return field;
    },
    get files() {
        return field.state ? field.state : [];
    },
    get remainingSlots() {
        return this.getMaxFiles() - this.files.length;
    },
    get maxExceeded() {
        const maxFiles = field.props.multiple ? this.getMaxFiles() : 1;

        return this.files.length >= maxFiles;
    },
    async selectFile() {
        const files = await selectFile({
            accept: field.props.accept,
            multiple: field.props.multiple,
        });

        if (files.length === 0) {
            return;
        }

        if (!field.props.multiple) {
            field.updateState({ value: [files[0]] });
            return;
        }

        field.updateState({ value: [...this.files, ...files] });
    },
    insertFile() {},
    removeFile(id) {
        if (this.files.length === 1) {
            this.field.updateState({ value: [] });
            return;
        }

        const index = this.files.findIndex(file => file.id === id);

        this.field.updateState({ value: undefined, statePath: String(index) });
    },
    async uploadPendingFiles() {
        if (props.disablePendingUploads) {
            return;
        }

        if (!this.field.state || this.field.state.length === 0) return;

        const pendingUploads = this.field.state.filter((file) => {
            return file.status == 0;
        });

        const uploads = pendingUploads.map(async (file) => {
            if (!file.__client) {
                file.__client = {};
            }

            let upload;

            if (!file.__client.upload_id) {
                const systemFile = getSystemFile(file.id);
                upload = await createUpload(systemFile, {
                    is_private: props.isPrivate,
                    crop: file.__client.crop,
                    clip: file.__client.clip,
                    original_name: file.original_name,
                });
                file.__client.upload_id = upload.id;
                this.field.updateState({
                    value: file,
                    statePath: String(this.field.state.findIndex(f => f.id === file.id)),
                });
            } else {
                upload = await Upload.query().find(file.__client.upload_id);
            }

            await startUpload(upload);

            this.field.updateState({
                value: upload.response.file,
                statePath: String(this.field.state.findIndex(f => f.id === file.id)),
            });

            deleteSystemFile(file.id);

            upload.delete();
        });

        const results = await Promise.allSettled(uploads);

        if (results.some((result) => result.status === 'rejected')) {
            console.error(results.filter((result) => result.status === 'rejected'));

            toast.error('Failed to upload all files');

            return false;
        }
    },
};

defineExpose(component);
</script>

<template>
    <FieldContainer>
        <slot />
    </FieldContainer>
</template>
